import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { SPACE_STRING } from '@glass/common/modules/strings/constants';
import isValue from '@glass/common/modules/utils/isValue';
import Typography from '@glass/web/components/base/Typography';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import emphasis from '@glass/shared/modules/theme/styles/emphasis';

const useStyles = makeStyles()((theme, { color, opacity }) => ({
  highlight: emphasis({ color: color || theme.palette.primary.light, opacity }),
}));

const useSplitTitle = ({ title, emphasisIndex }) =>
  useMemo(() => {
    if (!title || !isValue(emphasisIndex)) {
      return {};
    }

    const splitTitle = title?.split(SPACE_STRING);
    const index = Math.min(emphasisIndex || 0, splitTitle.length - 1);
    return {
      before: splitTitle?.slice(0, index).join(SPACE_STRING),
      highlight: splitTitle?.slice(index, emphasisIndex + 1)?.[0],
      after: splitTitle?.slice(index + 1).join(SPACE_STRING),
    };
  }, [title, emphasisIndex]);

function EmphasisTitle({
  title,
  emphasisIndex,
  componentColor,
  color,
  gutterBottom,
  variant,
  opacity,
}) {
  const { classes } = useStyles({ color, opacity });
  const { before, highlight, after } = useSplitTitle({ title, emphasisIndex });

  if (highlight) {
    return (
      <Typography color={componentColor} gutterBottom={gutterBottom} variant={variant}>
        {before ? <span>{`${before}${SPACE_STRING}`}</span> : null}
        {highlight ? <span className={classes.highlight}>{highlight}</span> : null}
        {after ? <span>{`${SPACE_STRING}${after}`}</span> : null}
      </Typography>
    );
  }

  if (title) {
    return (
      <Typography color={componentColor} gutterBottom={gutterBottom} variant={variant}>
        {title}
      </Typography>
    );
  }

  return null;
}

EmphasisTitle.propTypes = {
  color: PropTypes.string,
  componentColor: PropTypes.string,
  emphasisIndex: PropTypes.number,
  gutterBottom: PropTypes.bool,
  opacity: PropTypes.number,
  title: PropTypes.string,
  variant: PropTypes.string,
};

EmphasisTitle.defaultProps = {
  variant: 'h1',
  componentColor: 'inherit',
  opacity: 0.15,
};

export default EmphasisTitle;
